import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Space,
  message,
  Typography,
  Switch,
  Input,
} from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  getAllLmdSettings,
  updateLmdSetting,
  type LmdSetting,
} from "../../services/lmdRatesService";

const { Title, Text } = Typography;
const { TextArea } = Input;

const LMDSettingsTab: React.FC = () => {
  const [settings, setSettings] = useState<LmdSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  // Load settings
  const loadSettings = async () => {
    setLoading(true);
    try {
      const settingsData = await getAllLmdSettings();
      setSettings(settingsData);

      // Populate form with current values
      const formData: Record<string, any> = {};
      settingsData.forEach((setting) => {
        formData[setting.setting_key] = setting.setting_value;
        formData[`${setting.setting_key}_active`] = setting.is_active;
        formData[`${setting.setting_key}_description`] = setting.description;
      });
      form.setFieldsValue(formData);
    } catch (error) {
      console.error("Error loading LMD settings:", error);
      message.error("Failed to load LMD settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      const updatePromises: Promise<LmdSetting>[] = [];

      settings.forEach((setting) => {
        const newValue = values[setting.setting_key];
        const newActive = values[`${setting.setting_key}_active`];
        const newDescription = values[`${setting.setting_key}_description`];

        if (
          newValue !== setting.setting_value ||
          newActive !== setting.is_active ||
          newDescription !== setting.description
        ) {
          updatePromises.push(
            updateLmdSetting(setting.id, {
              setting_value: newValue,
              is_active: newActive,
              description: newDescription,
            })
          );
        }
      });

      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        message.success("LMD settings updated successfully");
        loadSettings(); // Reload to get updated data
      } else {
        message.info("No changes to save");
      }
    } catch (error) {
      console.error("Error saving LMD settings:", error);
      message.error("Failed to save LMD settings");
    } finally {
      setSaving(false);
    }
  };

  const getSettingInfo = (settingKey: string) => {
    const info: Record<
      string,
      {
        label: string;
        suffix?: string;
        precision?: number;
        min?: number;
        max?: number;
      }
    > = {
      diesel_surcharge_percentage: {
        label: "Diesel Surcharge",
        suffix: "%",
        precision: 2,
        min: 0,
        max: 100,
      },
      cartons_per_pallet: {
        label: "Cartons per Pallet",
        suffix: "cartons",
        precision: 0,
        min: 1,
        max: 50,
      },
      pallet_threshold: {
        label: "Pallet Threshold",
        suffix: "cartons",
        precision: 0,
        min: 1,
        max: 20,
      },
      nl_pallet_surcharge_eur: {
        label: "Tol surcharge",
        suffix: "€/pallet",
        precision: 2,
        min: 0,
        max: 100,
      },
    };
    return info[settingKey] || { label: settingKey };
  };

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>
              ⚙️ LMD Settings
            </Title>
            <Text type="secondary">
              Configure Last Mile Delivery calculation parameters
            </Text>
          </Space>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <div style={{ display: "grid", gap: "16px" }}>
            {settings
              .filter((setting) => setting.setting_key !== "own_delivery_enabled")
              .map((setting) => {
              const info = getSettingInfo(setting.setting_key);
              return (
                <Card
                  key={setting.setting_key}
                  size="small"
                  style={{ backgroundColor: "#fafafa" }}
                  title={
                    <Space>
                      <Text strong>{info.label}</Text>
                      <Form.Item
                        name={`${setting.setting_key}_active`}
                        valuePropName="checked"
                        style={{ margin: 0 }}
                      >
                        <Switch
                          size="small"
                          checkedChildren="Active"
                          unCheckedChildren="Inactive"
                        />
                      </Form.Item>
                    </Space>
                  }
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 2fr",
                        gap: "16px",
                        alignItems: "start",
                      }}
                    >
                      <Form.Item
                        name={setting.setting_key}
                        label="Value"
                        rules={[
                          { required: true, message: "Please enter a value" },
                          {
                            type: "number",
                            min: info.min || 0,
                            message: `Minimum value is ${info.min || 0}`,
                          },
                          ...(info.max
                            ? [
                                {
                                  type: "number" as const,
                                  max: info.max,
                                  message: `Maximum value is ${info.max}`,
                                },
                              ]
                            : []),
                        ]}
                        style={{ margin: 0 }}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          precision={info.precision || 2}
                          min={info.min || 0}
                          max={info.max}
                          step={info.precision === 0 ? 1 : 0.01}
                          addonAfter={info.suffix}
                        />
                      </Form.Item>

                      <Form.Item
                        name={`${setting.setting_key}_description`}
                        label="Description"
                        style={{ margin: 0 }}
                      >
                        <TextArea
                          rows={2}
                          placeholder="Optional description..."
                          style={{ fontSize: "12px" }}
                        />
                      </Form.Item>
                    </div>

                    <div style={{ fontSize: "11px", color: "#666" }}>
                      <Text>
                        <strong>Key:</strong> {setting.setting_key} |
                        <strong> Type:</strong> {setting.setting_type} |
                        <strong> Last updated:</strong>{" "}
                        {new Date(setting.updated_at).toLocaleString()}
                      </Text>
                    </div>
                  </Space>
                </Card>
              );
            })}
          </div>

          <div style={{ marginTop: 24, textAlign: "right" }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadSettings}
                disabled={loading || saving}
              >
                Reload
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                disabled={loading}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        </Form>

        <div
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: "#f0f0f0",
            borderRadius: 4,
          }}
        >
          <Text type="secondary" style={{ fontSize: "12px" }}>
            💡 <strong>Settings Info:</strong>
            <br />• <strong>Diesel Surcharge:</strong> Percentage added to base
            LMD rates when diesel surcharge is enabled
            <br />• <strong>Cartons per Pallet:</strong> Number of cartons that
            make up one pallet for calculation purposes
            <br />• <strong>Pallet Threshold:</strong> Carton count above which
            pallet rates are automatically suggested (auto mode)
            <br />• <strong>Tol surcharge:</strong> Fixed fee per pallet
            added to LMD transport in the Netherlands and Belgium (pallet shipments only)
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default LMDSettingsTab;
